import { getLocale, translate } from '../i18n'
import { formatQuantity, type UnitSystem } from '../utils/units'
import type { SyncFailureState } from './syncEngine'

type AccountStatusInput = {
   hasSyncKey: boolean
   isRemoteRefreshInFlight: boolean
   syncFailure: SyncFailureState
   heightCm: number | null
   targetWeightKg: number | null
   targetMonthlyRunEffort: number | null
   unitSystem: UnitSystem
}

/**
 * Builds the account status message shown while cloud sync is failing and retrying.
 *
 * @param syncFailure - The current sync-failure state.
 * @returns The localized status message.
 */
function buildSyncFailureStatusMessage(syncFailure: SyncFailureState) {
   const attemptLabel =
      syncFailure.failureCount > 1 ?
         translate(' (attempt {count})', { count: syncFailure.failureCount })
      :  ''
   const secondsUntilRetry =
      syncFailure.nextRetryAt > Date.now() ?
         Math.max(0, Math.ceil((syncFailure.nextRetryAt - Date.now()) / 1000))
      :  0
   const retryMessage =
      secondsUntilRetry > 0 ?
         translate(" We'll retry automatically in {seconds} {unit}.", {
            seconds: secondsUntilRetry,
            unit: translate(secondsUntilRetry === 1 ? 'second' : 'seconds'),
         })
      :  translate(" We'll retry automatically shortly.")
   const lastAttemptMessage =
      syncFailure.lastFailureTimestamp ?
         ` ${translate('Last attempt {time}', {
            time: new Date(syncFailure.lastFailureTimestamp).toLocaleTimeString(
               getLocale(),
               {
                  hour: '2-digit',
                  minute: '2-digit',
               },
            ),
         })}.`
      :  ''
   return `${translate(
      'Sync key active, but the latest cloud update failed{attemptLabel}.',
      { attemptLabel },
   )}${retryMessage}${lastAttemptMessage}`
}

/**
 * Builds the account status message summarizing saved height and targets.
 *
 * @param heightCm - The saved height in cm, or null.
 * @param targetWeightKg - The saved target weight in kg, or null.
 * @param targetMonthlyRunEffort - The saved monthly run-effort target, or null.
 * @param unitSystem - The unit system to display values in.
 * @returns The localized status message.
 */
function buildProfileStatusMessage(
   heightCm: number | null,
   targetWeightKg: number | null,
   targetMonthlyRunEffort: number | null,
   unitSystem: UnitSystem,
) {
   const heightMessage =
      typeof heightCm === 'number' ?
         translate('Height saved: {height}.', {
            height: formatQuantity('length', heightCm, unitSystem, {
               digits: heightCm % 1 === 0 && unitSystem === 'metric' ? 0 : 1,
            }),
         })
      :  translate('Add your height to unlock BMI insights.')
   const targetMessage =
      typeof targetWeightKg === 'number' ?
         ` ${translate('Target weight set to {weight}.', {
            weight: formatQuantity('mass', targetWeightKg, unitSystem),
         })}`
      :  ` ${translate('Set a target weight to track your goal in the charts.')}`
   const effortMessage =
      typeof targetMonthlyRunEffort === 'number' ?
         ` ${translate('Monthly run effort target: {value}.', {
            value: targetMonthlyRunEffort.toLocaleString(getLocale(), {
               maximumFractionDigits: 1,
            }),
         })}`
      :  ''
   return `${translate('Sync key active.')} ${heightMessage}${targetMessage}${effortMessage}`
}

/**
 * Builds the account status message for the current sync/profile state.
 *
 * @param input - The sync-key, refresh, failure, and profile state.
 * @returns The status message.
 */
export function buildAccountStatusMessage(input: AccountStatusInput) {
   if (!input.hasSyncKey) {
      return 'Create an anonymous synchronization key to back up data, or input an existing key to restore it on this device.'
   }
   if (input.isRemoteRefreshInFlight) {
      return translate('Updating...')
   }
   if (input.syncFailure.failureCount > 0) {
      return buildSyncFailureStatusMessage(input.syncFailure)
   }
   return buildProfileStatusMessage(
      input.heightCm,
      input.targetWeightKg,
      input.targetMonthlyRunEffort,
      input.unitSystem,
   )
}
